import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  FileText,
  CreditCard,
  MoreHorizontal,
  RefreshCw,
  HelpCircle,
  AlertTriangle,
  Users
} from 'lucide-react';
import { useToast } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ContractService from '../../services/contractService';
import { contractFinanceService } from '../../services/contractFinanceService';

/**
 * IMPORTANT – HOW THIS MODULE RELATES TO finance/ContractFinance
 * ----------------------------------------------------------------
 * - This screen is a subcontracting‑oriented VIEW over the same
 *   contract‑payment data that powers the Finance → ContractFinance
 *   module.
 * - It intentionally reuses the shared `contractFinanceService`
 *   and the unified backend routes:
 *     GET /finance/contracts/payments
 *     DELETE /finance/contracts/payments/:paymentId
 *   So there is NO separate \"subcontracting\" payment API.
 *
 * CONFLICT FIX:
 * - Previously it looked like `subcontracting/BillingPayments` and
 *   `finance/ContractFinance` were \"different\" billing modules,
 *   but in reality they should both talk to the SAME contract
 *   finance backend.
 * - The backend routes have now been normalised to `/payments`
 *   under `/finance/contracts` and this component uses only the
 *   shared `contractFinanceService` for reads and deletes.
 * - Creation / editing of payments is owned by the Finance module;
 *   this screen now behaves as a read‑oriented operational view
 *   for subcontracting users.
 */

const BillingPayments = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [loading, setLoading] = useState(false);
  const [billingData, setBillingData] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [paymentTotals, setPaymentTotals] = useState({ totalPaid: 0, outstanding: 0 });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });

  // ActionDropdown component for actions
  const ActionDropdown = ({ billing, onView, onEdit, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      const handleScroll = () => {
        setIsOpen(false);
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('scroll', handleScroll, true);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('scroll', handleScroll, true);
      };
    }, [isOpen]);

    const handleToggle = () => {
      if (!isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const menuHeight = 160;
        setOpenUpward(spaceBelow < menuHeight);
      }
      setIsOpen(!isOpen);
    };

    const handleAction = (cb) => {
      setIsOpen(false);
      cb(billing);
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={handleToggle}
          type="button"
          className="relative inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-[var(--gold-primary)] rounded hover:bg-[#FFF9E6] hover:border-[var(--gold-primary)] transition-colors shadow-sm"
        >
          <MoreHorizontal size={16} className="text-[var(--gold-primary)]" />
        </button>

        {isOpen && (
          <div
            className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-[10001]"
            style={{
              [openUpward ? 'bottom' : 'top']: openUpward
                ? `${window.innerHeight -
                buttonRef.current.getBoundingClientRect().top +
                8
                }px`
                : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
              left: `${buttonRef.current.getBoundingClientRect().left}px`,
              transform: 'translateX(-75%)',
            }}
          >
            <ul className="py-1 m-0 list-none">
              <li>
                <button
                  onClick={() => handleAction(onView)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                >
                  <Eye size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                  View Details
                </button>
              </li>

              <li>
                <button
                  onClick={() => handleAction(onEdit)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                >
                  <Edit size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                  Edit
                </button>
              </li>

              <li className="border-t border-gray-200">
                <button
                  onClick={() => handleAction(onDelete)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-50 border-none cursor-pointer transition-colors text-left"
                >
                  <Trash2 size={16} className="mr-2.5 text-red-600 flex-shrink-0" />
                  Delete
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  const formatCurrency = (value = 0, currency = 'USD') => (
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value || 0)
  );

  const transformPaymentForDisplay = (payment) => {
    const amount = Number(payment.amount || 0);
    const paidAmount = payment.status === 'Paid' ? amount : 0;
    const balance = Math.max(amount - paidAmount, 0);
    return {
      id: payment._id || payment.paymentId,
      invoiceNumber: payment.invoiceNumber || payment.paymentId || 'Payment',
      contractTitle: payment.contractTitle,
      subcontractor: payment.counterparty,
      description: payment.notes || payment.paymentType,
      paymentType: payment.paymentType,
      amount,
      paidAmount,
      balance,
      status: payment.status,
      paymentMethod: payment.paymentMethod,
      dueDate: payment.dueDate,
      paymentDate: payment.paidDate,
      notes: payment.notes,
      workOrder: payment.project?.name || '',
      currency: payment.currency || 'USD'
    };
  };

  const loadContracts = async () => {
    try {
      const response = await ContractService.getContracts();
      if (response.success) {
        const contractsList = (response.data?.contracts || response.data || [])
          .filter(contract => contract._id);
        const normalized = contractsList.map(contract => ({
          ...contract,
          displayLabel: `${contract.title || contract.contractId || 'Contract'}${contract.contractId ? ` (${contract.contractId})` : ''}`
        }));
        setContracts(normalized);
        if (!selectedContractId && normalized.length > 0) {
          setSelectedContractId(normalized[0]._id);
        }
      } else {
        showError(response?.message || 'Failed to load contracts');
      }
    } catch (err) {
      console.error('Error loading contracts:', err);
      showError(err.message || 'Failed to load contracts');
    }
  };

  const loadPayments = async (contractId, page = pagination.currentPage, limit = pagination.itemsPerPage) => {
    if (!contractId) {
      setBillingData([]);
      setPaymentTotals({ totalPaid: 0, outstanding: 0 });
      return;
    }
    setLoading(true);
    try {
      const response = await contractFinanceService.getContractPayments(contractId, { page, limit });
      if (response.success) {
        const payload = response.data || {};
        const payments = (payload.payments || []).map(transformPaymentForDisplay);
        setBillingData(payments);
        setPaymentTotals({
          totalPaid: Number(payload.totalPaid) || 0,
          outstanding: Number(payload.outstanding) || 0
        });
        setPagination(prev => ({
          ...prev,
          currentPage: payload.pagination?.current || page,
          totalItems: payload.pagination?.total || payments.length,
          totalPages: payload.pagination?.pages || prev.totalPages,
          itemsPerPage: limit
        }));
      } else {
        showError(response?.message || 'Failed to load billing data');
      }
    } catch (err) {
      console.error('Error loading billing payments:', err);
      showError(err.message || 'Failed to load billing payments');
    } finally {
      setLoading(false);
    }
  };

  const handleContractChange = (event) => {
    const value = event.target.value;
    setSelectedContractId(value);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleRefresh = () => {
    if (selectedContractId) {
      loadPayments(selectedContractId, pagination.currentPage, pagination.itemsPerPage);
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    if (selectedContractId) {
      loadPayments(selectedContractId, pagination.currentPage, pagination.itemsPerPage);
    }
  }, [selectedContractId, pagination.currentPage, pagination.itemsPerPage]);

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/billing-payments';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh billing data',
      disabled: loading
    },
    // NOTE: creation of contract payments/invoices is centralised in the
    // Finance → ContractFinance module to avoid two UIs sending different
    // payload shapes to the same backend routes. If subcontracting users
    // need to create invoices, you can navigate them into the finance
    // screen instead of maintaining a second conflicting form here.
  ];

  const handleView = (billing) => {
    navigate(`/subcontracting/billing/view/${billing.id}`);
  };

  // Editing of payments is intentionally handled in the finance/ContractFinance
  // module to keep one authoritative form structure for the shared backend.
  const handleEdit = (billing) => {
    navigate(`/finance/contract-finance?contractId=${billing.id}`);
  };

  const handleDelete = async (billing) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${billing.invoiceNumber}?`)) {
      return;
    }
    setLoading(true);
    try {
      await contractFinanceService.deleteContractPayment(billing.id);
      success('Invoice deleted successfully!');
      if (selectedContractId) {
        loadPayments(selectedContractId, pagination.currentPage, pagination.itemsPerPage);
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      showError(err.message || 'Failed to delete invoice');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Paid': { class: 'bg-success', text: 'Paid' },
      'Partial': { class: 'bg-warning', text: 'Partial' },
      'Pending': { class: 'bg-info', text: 'Pending' },
      'Overdue': { class: 'bg-danger', text: 'Overdue' },
      'Cancelled': { class: 'bg-secondary', text: 'Cancelled' }
    };

    const config = statusConfig[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'Bank Transfer':
        return <CreditCard size={16} className="text-primary" />;
      case 'Check':
        return <FileText size={16} className="text-info" />;
      case 'Credit Card':
        return <CreditCard size={16} className="text-success" />;
      default:
        return <DollarSign size={16} className="text-muted" />;
    }
  };

  const selectedContract = contracts.find((contract) => contract._id === selectedContractId);

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice',
      render: (value, billing) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '180px' }} title={billing.invoiceNumber}>
            {billing.invoiceNumber}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '180px' }}>
            {billing.paymentType || 'Payment'}
          </small>
        </div>
      )
    },
    {
      key: 'contractTitle',
      label: 'Contract',
      render: (value, billing) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '200px' }} title={billing.contractTitle || billing.subcontractor}>
            {billing.contractTitle || billing.subcontractor || 'Contract'}
          </div>
          {billing.subcontractor && (
            <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }}>
              {billing.subcontractor}
            </small>
          )}
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (value, billing) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '220px' }} title={billing.description || billing.notes}>
            {billing.description || billing.notes || 'No description provided'}
          </div>
          {billing.workOrder && (
            <small className="text-muted text-truncate d-block" style={{ maxWidth: '220px' }} title={billing.workOrder}>
              Work Order: {billing.workOrder}
            </small>
          )}
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (value, billing) => {
        const currency = billing.currency || selectedContract?.currency || 'USD';
        return (
          <div>
            <div className="fw-bold text-truncate d-block" style={{ maxWidth: '130px' }} title={formatCurrency(billing.amount, currency)}>
              {formatCurrency(billing.amount, currency)}
            </div>
            <small className="text-muted" title={formatCurrency(billing.balance, currency)}>
              Balance: {formatCurrency(billing.balance, currency)}
            </small>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, billing) => getStatusBadge(billing.status)
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (value, billing) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '120px' }} title={billing.dueDate ? new Date(billing.dueDate).toLocaleDateString() : 'TBD'}>
            {billing.dueDate ? new Date(billing.dueDate).toLocaleDateString() : 'TBD'}
          </div>
          {billing.paymentDate && (
            <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={`Paid: ${new Date(billing.paymentDate).toLocaleDateString()}`}>
              Paid: {new Date(billing.paymentDate).toLocaleDateString()}
            </small>
          )}
        </div>
      )
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      render: (value, billing) => (
        <div className="d-flex align-items-center">
          {getPaymentMethodIcon(billing.paymentMethod)}
          <span className="ms-2 text-truncate d-block" style={{ maxWidth: '140px' }} title={billing.paymentMethod || 'N/A'}>
            {billing.paymentMethod || 'N/A'}
          </span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, billing) => {
        if (!billing || !billing.id) {
          console.error('Invalid billing data in render function:', billing);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown
            billing={billing}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Billing & Payments"
        description="Manage subcontractor billing, invoicing, and payment processing"
        actions={headerActions}
      />
      <div className="page-container">
        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
          {/* Contract Selector */}
          <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-11 w-11 items-center justify-center bg-[#FFF9E6]">
                  <FileText size={20} className="text-[var(--gold-primary)]" />
                </div>
                <div className="text-[15px] font-semibold text-[var(--text-primary)]">
                  Contract
                </div>
              </div>

              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                value={selectedContractId}
                onChange={handleContractChange}
                disabled={!contracts.length || loading}
              >
                <option value="" disabled>
                  {contracts.length ? 'Select a contract' : 'No contracts available'}
                </option>
                {contracts.map((contract) => (
                  <option key={contract._id} value={contract._id}>
                    {contract.displayLabel}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Counterparty Info */}
          <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FEF3C7]">
                <Users size={20} className="text-[#92400E]" />
              </div>

              <div>
                <div className="text-[18px] font-bold text-[var(--text-primary)]">
                  {selectedContract?.counterparty || 'No Contract Selected'}
                </div>

                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  {selectedContract
                    ? `${selectedContract.frequency || 'Frequency N/A'}${selectedContract.paymentTerms ? ` • ${selectedContract.paymentTerms}` : ''}`
                    : 'Select a contract to view details'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
          {/* Total Paid */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <DollarSign size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(paymentTotals.totalPaid, selectedContract?.currency || 'USD')}
                </div>

                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Paid
                </div>
              </div>
            </div>
          </div>

          {/* Outstanding */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FEF3C7]">
                <AlertTriangle size={20} className="text-[#92400E]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(paymentTotals.outstanding, selectedContract?.currency || 'USD')}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Outstanding
                </div>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <FileText size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {pagination.totalItems}
                </div>

                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Payments
                </div>
              </div>
            </div>
          </div>
        </div>

        <DataTable
          data={billingData}
          columns={columns}
          title="Billing & Payments"
          loading={loading}
          pagination={{
            currentPage: pagination.currentPage,
            total: pagination.totalItems,
            itemsPerPage: pagination.itemsPerPage,
            totalPages: pagination.totalPages
          }}
          onPageChange={handlePageChange}
          showActions={false}
          emptyMessage={
            selectedContractId
              ? 'No invoices found for this contract.'
              : 'Select a contract above to view invoices.'
          }
        />
      </div>
    </>
  );
};

export default BillingPayments;